import { FC, useCallback, useEffect, useState } from "react";
import { TDepositHistoryStatus } from "@/context/GummiwormContext/hooks/useDepositTxs";
import { useBrandName } from "@/hooks/useBrandName";

interface ProcessingStepProps {
  requestId: string | null;
  status: TDepositHistoryStatus | null;
  error: string | null;
  estimatedAbsorptionTime?: number;
  amountLabel?: string;
  txHash?: string;
}

const STAGES = [
  { label: "Submitted", activeLabel: "Submitting..." },
  { label: "Approved", activeLabel: "Waiting for approval" },
  { label: "Submitted", activeLabel: "Submitting..." },
  { label: "Available", activeLabel: "Waiting for settlement" },
];

/** Map backend status to the stage index that is currently active.
 *  Returns 4 when all stages are complete (absorbed).
 *
 *  Stage 0 – Submitting the deposit request → Submitted once seen in DB
 *  Stage 1 – Waiting for approval → Approved once refund tx assigned
 *  Stage 2 – Submitting the L1 tx → Submitted once on-chain
 *  Stage 3 – Waiting for settlement → Available once absorbed
 */
function getActiveStageIndex(status: TDepositHistoryStatus | null): number {
  switch (status) {
    case null:
    case "new":
      return 0; // building / sending deposit request
    case "pending":
      return 1; // registered in DB, waiting for approval
    case "confirmed":
    case "settling":
      return 2; // approved, submitting L1 tx
    case "on-chain":
      return 3; // L1 confirmed, waiting for absorption
    case "absorbed":
      return 4; // all done
    default:
      return 0;
  }
}

function CopyButton({ text }: { text: string }) {
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="text-sr-pink-muted hover:text-sr-pink-light transition-colors"
      title="Copy"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
    </button>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="#170B10"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type BadgeVariant = "done" | "active" | "waiting";

function StatusBadge({ variant }: { variant: BadgeVariant }) {
  const config = {
    done: { label: "Done", className: "bg-sr-mint/15 text-sr-mint" },
    active: { label: "In progress", className: "bg-sr-tangerine/15 text-sr-tangerine" },
    waiting: { label: "Waiting", className: "bg-sr-neutral-700/30 text-sr-neutral-700" },
  };
  const { label, className } = config[variant];
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${className}`}>
      {label}
    </span>
  );
}

function formatCountdown(seconds: number): string {
  if (seconds <= 0) return "any moment now";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m > 0) return `~${m}m ${s.toString().padStart(2, "0")}s`;
  return `~${s}s`;
}

export const ProcessingStep: FC<ProcessingStepProps> = ({
  requestId,
  status,
  error,
  estimatedAbsorptionTime,
  amountLabel,
  txHash,
}) => {
  const brandName = useBrandName();
  const isFailed = status === "failed" || status === "rejected";
  const isComplete = status === "absorbed";
  const activeIdx = getActiveStageIndex(status);

  // Live countdown
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!estimatedAbsorptionTime) {
      setRemaining(null);
      return;
    }
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      setRemaining(Math.max(0, estimatedAbsorptionTime - now));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [estimatedAbsorptionTime]);

  return (
    <div className="sr-card w-full rounded-[32px] p-8">
      {/* Header row */}
      <div className="flex items-start justify-between mb-6">
        <h2
          className="text-[22px] font-medium text-white tracking-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {isFailed
            ? "Deposit Failed"
            : isComplete
              ? "Funds Deposited!"
              : "Deposit in Progress"}
        </h2>
      </div>

      {/* Deposit info */}
      {amountLabel && (
        <div className="flex flex-col gap-1.5 mb-8">
          <p className="text-lg tracking-tight">
            <span className="text-sr-pink-muted">Deposit:</span>{" "}
            <span className="text-white font-medium">
              {amountLabel}
            </span>
          </p>
          <p className="text-sm tracking-tight">
            <span className="text-sr-pink-muted">To:</span>{" "}
            <span className="text-sr-bubblegum">{brandName === "Sugar Rush" ? "Sugar Rush Trading Vault" : "Gummiworm Vault"}</span>
          </p>
        </div>
      )}

      {/* Countdown */}
      {!isFailed && !isComplete && remaining !== null && (
        <div className="flex items-center gap-2 mb-6">
          <span
            className="text-lg font-medium text-sr-lemon-muted tabular-nums"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {formatCountdown(remaining)}
          </span>
          <span className="text-xs text-sr-neutral-500">
            estimated time remaining
          </span>
        </div>
      )}

      {/* Vertical stepper */}
      <div className="flex flex-col">
        {STAGES.map((stage, i) => {
          const isDone = activeIdx > i;
          const isActive = activeIdx === i && !isFailed;
          const variant: BadgeVariant = isDone
            ? "done"
            : isActive
              ? "active"
              : "waiting";

          return (
            <div key={i} className="flex gap-4">
              {/* Icon + connector */}
              <div className="flex flex-col items-center">
                {isDone ? (
                  <div className="w-8 h-8 rounded-full bg-sr-mint flex items-center justify-center shrink-0">
                    <CheckIcon />
                  </div>
                ) : isActive ? (
                  <div className="w-8 h-8 rounded-full border-2 border-sr-tangerine border-t-transparent animate-spin shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-full border border-sr-neutral-700 shrink-0" />
                )}
                {i < STAGES.length - 1 && (
                  <div
                    className={`w-px flex-1 min-h-[24px] ${
                      isDone ? "bg-sr-mint/30" : "bg-sr-neutral-700/30"
                    }`}
                  />
                )}
              </div>

              {/* Content */}
              <div className={`flex-1 ${i < STAGES.length - 1 ? "pb-5" : ""}`}>
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm font-medium ${
                      isDone || isActive ? "text-white" : "text-sr-neutral-700"
                    }`}
                  >
                    {isActive ? stage.activeLabel : stage.label}
                  </span>
                  {i === 3 && isComplete ? (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sr-mint/15 text-sr-mint">
                      Complete!
                    </span>
                  ) : (
                    <StatusBadge variant={variant} />
                  )}
                </div>

                {/* Stage details */}
                {i === 0 && (isDone || isActive) && requestId && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="text-xs text-sr-pink-muted">
                      Request ID: {requestId}
                    </span>
                    <CopyButton text={requestId} />
                  </div>
                )}
                {i === 2 && isDone && txHash && (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="text-xs text-sr-pink-muted">
                      {txHash.slice(0, 8)}...{txHash.slice(-8)}
                    </span>
                    <CopyButton text={txHash} />
                    <a
                      href={`https://${window.__APP_CONFIG.env === "mainnet" ? "" : "preview."}cexplorer.io/tx/${txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sr-pink-muted hover:text-sr-pink-light transition-colors"
                    >
                      <ExternalLinkIcon />
                    </a>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {isFailed && error && (
        <div className="mt-6 rounded-xl bg-sr-salmon/10 p-3">
          <p className="text-xs text-sr-salmon">{error}</p>
        </div>
      )}
    </div>
  );
};
